import React, { useRef, useState } from 'react';
import Sketch from 'react-p5';
import accountStore from '../../../components/account/AccountStore';
import PlayerService from '../../../services/PlayerService';
import { formatRealityLayer } from '../../../utils/engine/RealityLayerFormatter';
import FrequencyScale from './FrequencyScale';
import audioManager from '../../../utils/filemanager/AudioManager';
import StatusDisplay from './StatusDisplay';
import SineWaveGraph from './SineWaveGraph';
import RadarScan from './RadarScan';
import CRTEffect from './CRTEffect';
import QuantumDataDisplay from './QuantumDataDisplay';
import QuantumLinkAnimation from './QuantumLinkAnimation';
import quantumAnalysisService from '../../../utils/engine/QuantumAnalysisService';
import { drawCallSign, INITIAL_STATE, CURSOR_BLINK_RATE, LETTER_FADE_DELAY } from './CallSignGenerator';
import { generateCallSign, FREQUENCY_CONSTANTS } from '../../../utils/engine/ProbabilityUtils';
import quantumEngine from '../../../utils/engine/QuantumEngine';
import { formatDilatedTime } from '../../../utils/engine/DilatedTimeFormatter';
import { QubitHarvestingSystem } from '../../../utils/game/qubits';

// Enum for states
const State = {
  SCANNING: 'SCANNING',
  SIGNAL_FOUND: 'SIGNAL_FOUND',
  DISPLAYING_CALL: 'DISPLAYING_CALL',
  ANALYZING_SIGNAL: 'ANALYZING_SIGNAL',
  SHOWING_ANALYSIS: 'SHOWING_ANALYSIS'
};

// Layout constants
const MARGINS = 40;

const P5Sketch = ({ onClose, linkedProfile, onUpdateProfile, selectedLayer, selectedSeed }) => {
  // Update database with new qubit count using AccountStore
  const updateQubitCount = async (newCount) => {
    if (linkedProfile && linkedProfile.playerId) {
      try {
        // Update the player data
        const currentProfile = accountStore.getProfile();
        await PlayerService.updatePlayerData(linkedProfile.playerId, {
          ...currentProfile.data,
          qubits: newCount
        });
        
        // Refresh profile data through AccountStore
        const freshProfile = await accountStore.refreshProfile();
        if (freshProfile && freshProfile.data) {
          onUpdateProfile(freshProfile);
        }
      } catch (error) {
        console.error('Error updating qubits in database:', error);
      }
    }
  };

  // Update database after harvesting session completes
  const onHarvestComplete = async (harvestResult) => {
    if (linkedProfile && harvestResult.totalHarvested !== linkedProfile.data.qubits) {
      await updateQubitCount(harvestResult.totalHarvested);
    }
  };
  const canvasRef = useRef(null);
  const [currentState, setCurrentState] = useState(State.SCANNING);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // State variables
  let scanTimer = 0;
  let signalLockStrength = 0;
  let frequency = quantumEngine.getCurrentState().frequency; // Get initial frequency from quantum engine
  let detectedFreq = null; // Will be set when signal is detected
  let minFreq = FREQUENCY_CONSTANTS.MIN_FREQ;
  let maxFreq = FREQUENCY_CONSTANTS.MAX_FREQ;
  let snr = 0;
  let qFactor = 0;
  let bandwidth = 2.4;
  let signalStrength = 0;
  let entanglement_coefficient = 0;
  let quantum_coherence = 0;
  let quantum_phase = 0;
  let pulseAngle = 0;
  const [callSignState, setCallSignState] = useState({ ...INITIAL_STATE });

  const initAudio = async () => {
    try {
      await audioManager.init();
      await audioManager.playPing();

    } catch (error) {
      console.error('Error during audio initialization:', error);
      throw error;
    }
  };

  const setup = (p5, canvasParentRef) => {
    // Initialize quantum engine with p5 frame count
    quantumEngine.frameCount = p5.frameCount;
    
    // Subscribe to quantum engine updates
    const unsubscribe = quantumEngine.subscribe(state => {
      // Update engine frame count based on p5 frame count
      quantumEngine.frameCount = p5.frameCount;
    });

    // Reset scanning state
    quantumEngine.resetScanning();

    // Cleanup subscription and stop simulation on component unmount
    window.addEventListener('beforeunload', () => {
      unsubscribe();
      quantumEngine.stopSimulation();
    });

    const setupCanvas = () => {
      const isMobile = window.innerWidth <= 768;
      const MAX_WIDTH = 1920;
      let w, h;
      
      if (isMobile) {
        // On mobile, use full screen width and adjust height for better visibility
        w = window.innerWidth;
        h = window.innerHeight;
      } else {
        w = Math.min(window.innerWidth, MAX_WIDTH);
        h = w * (9/16);
      }
      
      if (canvasRef.current) {
        canvasRef.current.remove();
      }
      
      canvasRef.current = p5.createCanvas(w, h).parent(canvasParentRef);
      
      const canvas = document.querySelector('canvas');
      if (canvas) {
        canvas.style.position = 'absolute';
        canvas.style.left = '50%';
        canvas.style.top = '50%';
        canvas.style.transform = 'translate(-50%, -50%)';
        canvas.style.maxWidth = '100vw';
        canvas.style.maxHeight = '100vh';
        canvas.style.touchAction = 'none';
        canvas.style.margin = 'auto';
      }
    };

    setupCanvas();
    setCallSignState(state => ({
      ...state,
      currentCallSign: generateCallSign(),
      lastUpdateTime: p5.millis(),
      cursorBlinkTime: p5.millis()
    }));
    
    // Initialize audio
    if (!audioManager.isInitialized()) {
      initAudio();
    }
    
    p5.textFont('IBM BIOS');
    
    document.addEventListener('click', async () => {
      if (!audioManager.isInitialized()) {

        try {
          await initAudio();
        } catch (error) {
          console.error('Failed to initialize audio:', error);
        }
      } else {
        await audioManager.resume();
      }
    });

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setupCanvas();
      }, 250);
    });
  };

  const drawCurrentLayer = (p5, engineState, state, layer) => {
    // Get initial state for the layer to get time dilation
    const layerState = quantumEngine.generateInitialState(layer);
    const timeDilation = layerState.metrics.timeDilation;
    const dilatedDateTime = formatDilatedTime(timeDilation);
    
    // Check if mobile
    const isMobile = p5.width <= 768;
    
    // Calculate position based on frequency scanner location
    const mobileMargins = isMobile ? 20 : MARGINS;
    const freqY = isMobile ? mobileMargins + 50 : mobileMargins + 100;
    const freqHeight = 40; // Height of frequency scanner
    
    // Position text below frequency scanner with padding
    const topMargin = isMobile ? p5.height * 0.05 : freqY + freqHeight + 40;
    const lineSpacing = isMobile ? p5.height * 0.035 : 30;
    
    p5.push();
    p5.fill('#00ff00');
    p5.textAlign(p5.CENTER);
    
    // Adjust text sizes based on screen size
    const seedSize = isMobile ? 24 : 32;
    const infoSize = isMobile ? 16 : 20;
    
    // Draw seed on top with larger size
    p5.textSize(seedSize);
    p5.text(`${selectedSeed}`, p5.width/2, topMargin);
    
    // Draw date and time underneath with smaller size
    p5.textSize(infoSize);
    p5.text(dilatedDateTime.date, p5.width/2, topMargin + lineSpacing);
    p5.text(dilatedDateTime.time, p5.width/2, topMargin + (lineSpacing * 2));
    
    p5.pop();
  };

  const draw = (p5) => {
    p5.background(0);
    pulseAngle += 0.02;
    scanTimer += 0.02;

    // Get current quantum state and update metrics
    const engineState = quantumEngine.getCurrentState();
    frequency = engineState.frequency;
    ({
      snr,
      qFactor,
      bandwidth,
      signalStrength,
      entanglement_coefficient,
      quantum_coherence,
      quantum_phase
    } = engineState);

    // Update quantum engine frame count
    quantumEngine.frameCount = p5.frameCount;

    // Function to handle signal detection
    const handleSignalDetection = () => {
      setCurrentState(State.SIGNAL_FOUND);
      signalLockStrength = 0;
      // Get detected frequency from quantum engine's scanning state
      detectedFreq = quantumEngine.scanningState.detectedFrequency;
  
      // Play alert sound when signal is found
      if (audioManager.isInitialized()) {
        audioManager.playAlert();
      }
    };
  
    // Update state
    switch(currentState) {
      case State.SCANNING:
        // Draw current layer identifier
        drawCurrentLayer(p5, engineState, State.SCANNING, selectedLayer);
  
        // Update quantum engine frame count
        quantumEngine.frameCount = p5.frameCount;
  
        // Play ping every 3 seconds (180 frames at 60fps)
        if (audioManager.isInitialized() && p5.frameCount % 180 === 0) {
          audioManager.playPing();
        }
  
        // Check for signal detection
        if (quantumEngine.detectSignal()) {
          handleSignalDetection();
        }
        break;

      case State.SIGNAL_FOUND:
        // Update signal lock strength
        quantumEngine.scanningState.signalLockStrength = Math.min(1, quantumEngine.scanningState.signalLockStrength + 0.05);
        signalLockStrength = quantumEngine.scanningState.signalLockStrength;
        
        // Draw current layer identifier
        drawCurrentLayer(p5, engineState, State.SIGNAL_FOUND, selectedLayer);

        // Once signal is fully locked, generate callsign
        if (signalLockStrength >= 1) {
          const newCallSign = generateCallSign();
          setCallSignState(state => ({
            ...state,
            currentCallSign: newCallSign,
            typing: true,
            currentLetter: 0,
            lastUpdateTime: p5.millis()
          }));
          setCurrentState(State.DISPLAYING_CALL);
        }
        break;

      case State.DISPLAYING_CALL:
        // Keep detected frequency from scanning state
        detectedFreq = quantumEngine.scanningState.detectedFrequency;
        // Update current scanning frequency
        frequency = engineState.frequency;
        snr = engineState.snr;
        qFactor = engineState.qFactor;
        bandwidth = engineState.bandwidth;
        signalStrength = engineState.signalStrength;
        entanglement_coefficient = engineState.entanglement_coefficient;
        quantum_coherence = engineState.quantum_coherence;
        quantum_phase = engineState.quantum_phase;

        if (callSignState.typing && p5.millis() - callSignState.lastUpdateTime > LETTER_FADE_DELAY) {
          if (callSignState.currentLetter < callSignState.currentCallSign.length) {
            if (audioManager.isInitialized()) {
              const currentChar = callSignState.currentCallSign[callSignState.currentLetter].toUpperCase();
              audioManager.playSound(currentChar).catch(error => {
                console.error(`Error playing sound for ${currentChar}:`, error);
              });
            }
            
            setCallSignState(state => ({
              ...state,
              currentLetter: state.currentLetter + 1,
              lastUpdateTime: p5.millis()
            }));
          } else {
            if (p5.millis() - callSignState.lastUpdateTime > 2000) {
              setCurrentState(State.ANALYZING_SIGNAL);
              setIsAnalyzing(true);
              setCallSignState(state => ({ ...state, typing: false }));
              
              // Get current quantum state from engine for analysis
              const engineState = quantumEngine.getCurrentState();
              const stateWithLayer = {
                ...engineState,
                realityLayer: selectedLayer
              };
              
              quantumAnalysisService.updateQuantumMetrics(stateWithLayer);
              quantumAnalysisService.analyzeQuantumData()
                .then(analysis => {
                  setCurrentAnalysis(analysis);
                  setCurrentState(State.SHOWING_ANALYSIS);
                  setIsAnalyzing(false);
                })
                .catch(error => {
                console.error('Error analyzing quantum data:', error);
                // Reset scanning state and return to scanning
                quantumEngine.resetScanning();
                detectedFreq = null;
                signalLockStrength = 0;
                setCurrentState(State.SCANNING);
                setIsAnalyzing(false);
              });
    
              // Keep quantum engine frame count synced with p5
              quantumEngine.frameCount = p5.frameCount;
            }
          }
        }
        break;

      case State.ANALYZING_SIGNAL:
        // Show loading state while analyzing
        break;

      case State.SHOWING_ANALYSIS:
        // Analysis display is handled by QuantumDataDisplay component
        break;

      default:
        break;
    }

    // Check if mobile
    const isMobile = p5.width <= 768;
    
    // Adjust margins for mobile
    const mobileMargins = isMobile ? 20 : MARGINS;
    
    // Draw radar with adjusted position for mobile
    const centerX = p5.width / 2;
    const centerY = isMobile ? p5.height / 3 : (p5.height / 2) + 120;
    const radarScale = isMobile ? 0.8 : 1;
    RadarScan({
      p5,
      width: p5.width * radarScale,
      height: p5.height * radarScale,
      pulseAngle,
      x: centerX,
      y: centerY,
      currentState
    });

    // Calculate display dimensions
    const displayWidth = p5.width - (mobileMargins * 2);

    // Draw frequency scale with adjusted position for mobile
    const freqY = isMobile ? mobileMargins + 50 : mobileMargins + 100;
    FrequencyScale(p5, mobileMargins, freqY, displayWidth, 40, engineState.frequency || frequency, minFreq, maxFreq, currentState, detectedFreq);
    
    // Section heights and positions adjusted for mobile
    const sineHeight = isMobile ? p5.height * 0.2 : p5.height * 0.25;
    const sineWidth = isMobile ? p5.width * 0.9 : p5.width * 0.7;
    const statusHeight = isMobile ? 35 : 45;
    
    const sineY = isMobile ? p5.height - sineHeight - 20 : p5.height - sineHeight - 30;
    const sineX = (p5.width - sineWidth) / 2;
    const statusY = sineY - statusHeight;

    // Adjust text sizes for mobile
    if (isMobile) {
      p5.textSize(p5.textSize() * 0.8);
    }

    // Get current harvested qubits count
    const harvestedQubits = quantumEngine.getHarvestedQubits();

    // Draw status display
    StatusDisplay(p5, currentState, signalLockStrength, p5.width, sineY,
      entanglement_coefficient, quantum_coherence, quantum_phase, MARGINS,
      frequency, snr, qFactor, bandwidth, harvestedQubits);

    // Draw sine wave
    SineWaveGraph({
      p5,
      x: sineX,
      y: sineY,
      w: sineWidth,
      h: sineHeight,
      currentState,
      signalLockStrength,
      frequency,
      bandwidth,
      qFactor,
      signalStrength
    });

    // Update cursor blink state
    if (p5.millis() - callSignState.cursorBlinkTime > CURSOR_BLINK_RATE) {
      setCallSignState(state => ({
        ...state,
        cursorVisible: !state.cursorVisible,
        cursorBlinkTime: p5.millis()
      }));
    }
    
    drawCallSign(p5, callSignState);
    
    // Draw quantum link animation if analyzing
    if (currentState === State.ANALYZING_SIGNAL) {
      const animWidth = isMobile ? p5.width * 0.8 : p5.width * 0.4;
      const animHeight = isMobile ? 120 : 150;
      const animX = (p5.width - animWidth) / 2;
      const animY = isMobile ? p5.height / 3 : (p5.height - animHeight) / 2;
      
      QuantumLinkAnimation({ p5, x: animX, y: animY, width: animWidth, height: animHeight });
    }
    
    // Apply CRT effect with reduced intensity on mobile for better performance
    CRTEffect({
      p5,
      width: p5.width,
      height: p5.height,
      intensity: isMobile ? 0.5 : 1 // Reduce effect intensity on mobile
    });
  };

  return (
    <>
      <div style={{
        position: 'relative',
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            zIndex: 1000,
            background: 'rgba(0, 255, 0, 0.2)',
            border: '2px solid #0f0',
            color: '#0f0',
            padding: '8px 16px',
            cursor: 'pointer',
            fontFamily: 'leaflings',
            fontSize: '14px',
            textTransform: 'uppercase',
            boxShadow: '0 0 10px rgba(0, 255, 0, 0.3)',
            transition: 'all 0.3s ease',
            textShadow: '0 0 5px #0f0'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(0, 255, 0, 0.3)';
            e.target.style.boxShadow = '0 0 15px rgba(0, 255, 0, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(0, 255, 0, 0.2)';
            e.target.style.boxShadow = '0 0 10px rgba(0, 255, 0, 0.3)';
          }}
        >
          ← Back
        </button>
        <Sketch setup={setup} draw={draw} />
      </div>
      {currentState === State.SHOWING_ANALYSIS && currentAnalysis && (
        <QuantumDataDisplay
          analysisData={currentAnalysis}
          onClose={() => {
            // Reset call sign state
            setCallSignState(state => ({
              ...state,
              currentLetter: 0,
              currentCallSign: generateCallSign(),
              lastUpdateTime: Date.now(),
              typing: false
            }));
            
            // Reset analysis state
            setCurrentAnalysis(null);
            
            // Reset quantum engine scanning
            quantumEngine.resetScanning();
            
            // Reset signal lock strength and detected frequency
            signalLockStrength = 0;
            detectedFreq = null;
            
            // Return to scanning state
            setCurrentState(State.SCANNING);
          }}
        />
      )}
    </>
  );
};

export default P5Sketch;